import { action, observable } from 'mobx';
import Collection from 'core/model/Collection';
import api from 'core/util/api';
import KentikAgentModel from './KentikAgentModel';

export default class KentikAgentCollection extends Collection {
  urlParams = {
    unregistered: true,
    registered: true,
    capabilityKey: null
  };

  @observable.ref
  memoryUtilQueryData = [];

  @observable.ref
  cpuUtilQueryData = [];

  constructor({ unregistered = true, registered = true, capabilityKey, ...restOptions } = {}) {
    super(undefined, restOptions);

    this.urlParams = {
      unregistered,
      registered
    };
    if (capabilityKey) {
      this.urlParams.capabilityKey = capabilityKey;
    }
  }

  get defaultGroupBy() {
    return 'groupRank';
  }

  get url() {
    // capabilityKey is assumed to be a single value for now
    const { unregistered, registered, capabilityKey } = this.urlParams;

    const baseUrl = `/api/ui/kentik-agent/agents?unregistered=${unregistered}&registered=${registered}`;
    if (capabilityKey) {
      return `${baseUrl}&capabilityKey=${capabilityKey}`;
    }
    return baseUrl;
  }

  get defaultSortState() {
    return {
      field: 'groupRank',
      direction: 'asc'
    };
  }

  get secondarySort() {
    return {
      field: 'status',
      direction: 'desc'
    };
  }

  get model() {
    return KentikAgentModel;
  }

  /**
   * @param {boolean} forceLoad
   * @returns {Promise<boolean>}
   */
  getHasClustersUnfiltered = (forceLoad) =>
    super.fetch(forceLoad).then(() => {
      const filteredbyClustered = this.filter((agent) => agent.hasClusterId, { immutable: true });

      return filteredbyClustered.length > 0;
    });

  @action
  fetchNew = () => {
    this.setRequestStatus('fetchingMore');
    return api.get(this.url).then(
      action((results) => {
        this.add(results.filter((result) => !this.findByInstallId(result.install_id)));
        this.setLastFetched();
        this.setRequestStatus(null);
        return results;
      })
    );
  };

  /**
   * Override fetch to also load capability metadata in parallel,
   * then filter each agent's capabilities based on featureFlag permissions.
   * @param {import('core/model/Collection').FetchOptions} options
   * @returns {Promise}
   */
  fetch = (options = {}) => {
    const metadataPromise = this.store?.$kentikAgents?.fetchAllCapabilityMetadata?.() || Promise.resolve({});

    return Promise.all([super.fetch(options), metadataPromise]).then(([fetchResult, metadata]) => {
      this.filterCapabilitiesByFeatureFlag(metadata);
      return fetchResult;
    });
  };

  /**
   * Remove capabilities from each agent model where the metadata's featureFlag
   * is not permitted for the current user.
   * @param {Record<string, { featureFlag?: string }>} metadata - capability metadata keyed by capability name
   */
  @action
  filterCapabilitiesByFeatureFlag(metadata) {
    const authStore = this.store?.$auth;

    if (!authStore || !metadata || Object.keys(metadata).length === 0) {
      return;
    }

    this.each((agent) => {
      const capabilities = agent.get('capabilities');

      if (!Array.isArray(capabilities)) {
        return;
      }

      const filtered = capabilities.filter((capability) => {
        const capMetadata = metadata[capability.name];
        const featureFlag = capMetadata?.featureFlag;

        // Keep the capability if there's no metadata or no featureFlag requirement
        if (!featureFlag) {
          return true;
        }

        return authStore.hasPermission(featureFlag, { overrideForSudo: false });
      });

      if (filtered.length !== capabilities.length) {
        agent.set('capabilities', filtered);
      }
    });
  }

  findByInstallId(agentId) {
    return this.get().find((item) => item.get('install_id') === agentId);
  }

  findByAgentId(agentId) {
    return this.get().find((item) => item.get('agent_id') === agentId);
  }

  setMemoryUtilQueryData = () => {
    this.memoryUtilQueryData.forEach((result) => {
      const device = this.get(result.agent_id);
      if (device) {
        device.set('memory_util', result.last_kagent_memory_utilization);
      }
    });
  };

  setCpuUtilQueryData = () => {
    this.cpuUtilQueryData.forEach((result) => {
      const device = this.get(result.agent_id);
      if (device) {
        device.set('cpu_util', result.last_kagent_cpu_utilization);
      }
    });
  };
}
